import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErrorMessage, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { Printer, WhatsappLogo, CaretLeft } from "@phosphor-icons/react";
import { BRAND_LOGO_URL, BRAND_NAME } from "@/lib/brand";
import { buildWhatsAppUrl, receiptShareMessage } from "@/lib/whatsapp";

const PAPER_SIZES = {
    a4:  { label: "A4",   className: "print-a4"  },
    "80": { label: "80mm", className: "print-80" },
    "58": { label: "58mm", className: "print-58" },
};

const MODE_LABELS = { cash: "Cash", upi: "UPI", bank_transfer: "Bank Transfer", cheque: "Cheque", other: "Other" };

export default function Receipt() {
    const { paymentId } = useParams();
    const navigate = useNavigate();
    const [payment, setPayment] = useState(null);
    const [paper, setPaper] = useState("a4");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/payments/${paymentId}`);
                setPayment(data);
            } catch (e) { toast.error(apiErrorMessage(e)); }
        })();
    }, [paymentId]);

    if (!payment) return <div className="p-8 text-sm text-neutral-500">Loading receipt...</div>;

    const customer = payment.customer;
    const balance = payment.sale_balance ?? 0;

    const shareWhatsApp = () => {
        if (!customer) return toast.error("Add a customer to share via WhatsApp");
        const msg = receiptShareMessage({
            customerName: customer.name,
            receiptNo: payment.receipt_no,
            amount: payment.amount,
            invoiceNo: payment.invoice_no,
            mode: payment.payment_mode,
            balance: balance,
            receiptUrl: window.location.href,
        });
        window.open(buildWhatsAppUrl(customer.whatsapp || customer.mobile, msg), "_blank");
    };

    return (
        <div className="min-h-screen bg-neutral-100">
            <div className="no-print sticky top-0 z-20 bg-white border-b border-neutral-200 px-4 md:px-8 py-3 flex flex-wrap items-center gap-3">
                <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900" data-testid="receipt-back">
                    <CaretLeft size={16} /> Back
                </button>
                <div className="text-sm text-neutral-500 hidden md:block">Receipt · <span className="font-mono font-semibold text-neutral-900">{payment.receipt_no}</span></div>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                    <div className="flex gap-1 border border-neutral-200 rounded p-0.5">
                        {Object.entries(PAPER_SIZES).map(([k, v]) => (
                            <button key={k} onClick={() => setPaper(k)}
                                    className={"px-3 py-1 text-xs font-semibold rounded " + (paper === k ? "bg-black text-white" : "text-neutral-600 hover:text-neutral-900")}
                                    data-testid={`receipt-paper-${k}`}>
                                {v.label}
                            </button>
                        ))}
                    </div>
                    <Button variant="outline" onClick={shareWhatsApp} data-testid="receipt-whatsapp">
                        <WhatsappLogo size={14} weight="fill" className="mr-1.5" style={{ color: "#25D366" }} /> WhatsApp
                    </Button>
                    <Button onClick={() => window.print()} className="text-white" style={{ backgroundColor: "#15803D" }} data-testid="receipt-print">
                        <Printer size={14} className="mr-1.5" /> Print / PDF
                    </Button>
                </div>
            </div>

            <div className="print-area py-6 md:py-10 flex justify-center">
                <div className={"invoice-paper bg-white text-black shadow print-page " + PAPER_SIZES[paper].className}>
                    <ReceiptBody payment={payment} customer={customer} balance={balance} paper={paper} />
                </div>
            </div>
        </div>
    );
}

function ReceiptBody({ payment, customer, balance, paper }) {
    const isThermal = paper === "58" || paper === "80";
    return (
        <div className="p-5 md:p-8">
            <div className="text-center pb-3 border-b-2" style={{ borderColor: "#0A0A0A" }}>
                <div className="w-16 h-16 rounded overflow-hidden bg-white border border-neutral-200 mx-auto flex items-center justify-center mb-2">
                    <img src={BRAND_LOGO_URL} alt={BRAND_NAME} className="w-full h-full object-contain" />
                </div>
                <div className="text-lg font-extrabold font-display">{BRAND_NAME}</div>
                <div className="text-[9px] text-neutral-500 uppercase tracking-widest mt-0.5">Premium Fiber Craft</div>
                <div className="mt-3 inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: "#DCFCE7", color: "#15803D" }}>
                    Payment Receipt
                </div>
            </div>

            <div className="mt-4 flex items-start justify-between gap-3">
                <div>
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Receipt No.</div>
                    <div className="text-base font-extrabold font-mono">{payment.receipt_no}</div>
                </div>
                <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Date</div>
                    <div className="text-sm font-semibold">{formatDate(payment.payment_date)}</div>
                </div>
            </div>

            <div className="mt-4 pt-3 border-t border-neutral-200">
                <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold mb-1">Received From</div>
                <div className="font-bold text-sm">{payment.customer_name || "Walk-in Customer"}</div>
                {customer?.mobile && <div className="text-xs text-neutral-700 mt-0.5">📱 {customer.mobile}</div>}
                {customer?.email && <div className="text-xs text-neutral-700">✉ {customer.email}</div>}
                {customer?.gst_number && <div className="text-xs text-neutral-700 font-mono">GST: {customer.gst_number}</div>}
            </div>

            {payment.invoice_no && (
                <div className="mt-4 pt-3 border-t border-neutral-200 grid grid-cols-2 gap-3 text-xs">
                    <div>
                        <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Against Invoice</div>
                        <div className="font-mono font-bold mt-0.5">{payment.invoice_no}</div>
                    </div>
                    {payment.sale && (
                        <div className="text-right">
                            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Invoice Total</div>
                            <div className="tabular font-semibold mt-0.5">{formatCurrency(payment.sale.total)}</div>
                        </div>
                    )}
                </div>
            )}

            {/* Amount block */}
            <div className="mt-5 p-4 rounded-md border-2" style={{ borderColor: "#15803D", background: "#F0FDF4" }}>
                <div className="text-center">
                    <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Amount Received</div>
                    <div className="text-3xl font-extrabold font-display mt-1 tabular" style={{ color: "#15803D" }}>{formatCurrency(payment.amount)}</div>
                    <div className="mt-2 text-[10px] uppercase tracking-widest font-bold text-neutral-600">
                        Payment Mode: <span className="text-neutral-900">{MODE_LABELS[payment.payment_mode] || payment.payment_mode}</span>
                    </div>
                </div>
            </div>

            {(payment.reference_no || balance !== null) && (
                <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                    {payment.reference_no && (
                        <div>
                            <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Reference / Txn ID</div>
                            <div className="font-mono mt-0.5">{payment.reference_no}</div>
                        </div>
                    )}
                    <div className="text-right">
                        <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">Balance Due</div>
                        <div className="tabular font-bold mt-0.5" style={{ color: balance > 0 ? "#DC2626" : "#15803D" }}>{formatCurrency(balance)}</div>
                    </div>
                </div>
            )}

            {payment.notes && (
                <div className="mt-3 text-xs text-neutral-600 italic border-t border-neutral-200 pt-2">
                    Note: {payment.notes}
                </div>
            )}

            {!isThermal && (
                <div className="mt-5 flex justify-center">
                    <QRCodeSVG value={payment.receipt_no} size={64} bgColor="#ffffff" fgColor="#0A0A0A" />
                </div>
            )}

            <div className="mt-5 pt-4 border-t border-neutral-300 flex justify-between text-[10px] text-neutral-500">
                <div>
                    Received by: <b className="text-neutral-800">{payment.received_by_name}</b>
                </div>
                <div className="text-right">
                    <div className="h-6"></div>
                    <div className="border-t border-neutral-400 pt-1 min-w-[110px]">
                        <div className="text-[9px] uppercase tracking-wider">Authorised Signature</div>
                    </div>
                </div>
            </div>
            <div className="text-center mt-3 text-[9px] text-neutral-400 uppercase tracking-widest">
                Thank you for your payment · {BRAND_NAME}
            </div>
        </div>
    );
}
